/**
 * Shared surface chrome for operational primitives (loading/empty/partial/failed/denied).
 * Mobile-first: vertical stack; no hover-only actions.
 */

import React from 'react';
import { Alert, Spin, Typography } from 'antd';
import {
  PRIMITIVE_AUTHORITY_FLAGS,
  resolvePrimitiveAriaBusy,
  resolvePrimitiveStatusRole,
  type PrimitivePresentationState,
} from './primitiveSurfaceState';
import styles from './primitives.module.css';

const { Text, Paragraph } = Typography;

export interface PrimitiveSurfaceFrameProps {
  readonly title: string;
  readonly presentationState: PrimitivePresentationState;
  readonly emptyMessage?: string;
  readonly partialMessage?: string;
  readonly failedMessage?: string;
  readonly deniedMessage?: string;
  readonly 'data-testid'?: string;
  readonly 'aria-label'?: string;
  readonly children?: React.ReactNode;
}

export function PrimitiveSurfaceFrame(props: PrimitiveSurfaceFrameProps): React.ReactElement {
  const {
    title,
    presentationState,
    emptyMessage = '표시할 데이터가 없습니다.',
    partialMessage = '일부 데이터만 사용할 수 있습니다.',
    failedMessage = '데이터를 불러오지 못했습니다.',
    deniedMessage = '권한이 부족하여 표시할 수 없습니다.',
    children,
  } = props;

  const testId = props['data-testid'];
  const ariaLabel = props['aria-label'] ?? title;
  const role = resolvePrimitiveStatusRole(presentationState);
  const ariaBusy = resolvePrimitiveAriaBusy(presentationState);

  const shellProps = {
    className: styles.primitiveSurface,
    'data-testid': testId,
    'data-presentation-state': presentationState,
    'data-grants-authority': String(PRIMITIVE_AUTHORITY_FLAGS.grantsAuthority),
    'data-grants-approval': String(PRIMITIVE_AUTHORITY_FLAGS.grantsApproval),
    'aria-label': ariaLabel,
    'aria-busy': ariaBusy || undefined,
    role,
  } as const;

  if (presentationState === 'loading') {
    return (
      <section {...shellProps}>
        <Spin tip={`${title} 불러오는 중`} />
        <Text type="secondary">불러오는 중…</Text>
      </section>
    );
  }

  if (presentationState === 'empty') {
    return (
      <section {...shellProps}>
        <Paragraph style={{ marginBottom: 0 }}>{emptyMessage}</Paragraph>
      </section>
    );
  }

  if (presentationState === 'failed') {
    return (
      <section {...shellProps}>
        <Alert type="error" showIcon message={failedMessage} />
      </section>
    );
  }

  if (presentationState === 'denied') {
    return (
      <section {...shellProps}>
        <Alert type="warning" showIcon message={deniedMessage} />
      </section>
    );
  }

  if (presentationState === 'partial') {
    return (
      <section {...shellProps}>
        <Alert type="info" showIcon message={partialMessage} />
        {children}
      </section>
    );
  }

  return <section {...shellProps}>{children}</section>;
}
